<template>
  <div class="sidebar">
    <ul ref="sidebarRef" class="nav">
      <li
        v-for="(menuGroup, index) in menuStore.filteredMenuGroups"
        :id="`sidebar-vars.${index}`"
        :key="menuGroup.GroupCode"
        :class="{ active: index === menuStore.activeIndex }"
        @click="handleGroupClick(index)"
      >
        <span class="name">{{ getNameByLocale(menuGroup) }}</span>
        <div class="caret">
          <span v-show="index === menuStore.activeIndex" class="arrow"></span>
        </div>
      </li>
    </ul>
    <footer>
      <template v-if="configStore.config.IsFastOrderUsed">
        <button @click="handleHomeClick">
          <span><img src="@/assets/images/icon-home.svg" alt="홈 버튼 아이콘" /></span>
          <span>{{ t('button.home') }}</span>
        </button>
      </template>
      <template v-else-if="isCallButtonShown && !configStore.config.IsFastOrderUsed">
        <button @click="handleStaffCallClick">
          <span><img src="@/assets/images/icon-bell.png" alt="직원호출 버튼 아이콘" /></span>
          <span>{{ t('button.staff_call') }}</span>
        </button>
      </template>
    </footer>
  </div>
</template>

<script setup lang="ts">
  import { openServiceApp } from '@/api/android';
  import StaffCall from '@/components/StaffCall.vue';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { useModalStore } from '@/stores/modal';
  import { getNameByLocale } from '@/utils';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();

  const sidebarRef = ref<HTMLElement | null>(null);

  const isCallButtonShown = computed(() => menuStore.callMessages.length > 0);

  /**
   * 메뉴 그룹 클릭
   * @param code 선택한 메뉴 그룹 코드
   */
  const handleGroupClick = (index: number) => {
    //menuStore.setActiveIndex(index);

    const contentsElement = document.getElementById('contents');
    const menuGroupElement = document.getElementById(`menu-group-${index}`);
    if (contentsElement && menuGroupElement) {
      contentsElement.scrollTo({ top: (menuGroupElement as HTMLElement).offsetTop, behavior: 'smooth' });
    }
  };

  /** 직원호출 버튼 클릭 */
  const handleStaffCallClick = () => modalStore.openFullScreenModal(StaffCall, {});

  /** 패스트오더 서비스 앱 홈으로 돌아가기 */
  const handleHomeClick = () => openServiceApp();
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  $left-nav-bg-width: 21.4063vw;
  $left-nav-padding: 1.1719vw;
  $font-size: 1.7969vw;

  .sidebar {
    display: grid;
    grid-template-rows: 1fr auto;
    align-items: flex-start;
    width: 100%;
    height: calc(100vh - vars.$left-nav-header-height);
    padding: $left-nav-padding;
    padding-right: 0;
    background: linear-gradient(to right, var(--sidebar-bg) $left-nav-bg-width, transparent 0);

    ul.nav {
      position: relative;
      height: 100%;
      overflow-x: visible;
      overflow-y: auto;
      list-style: none;
      scroll-behavior: smooth;

      &::-webkit-scrollbar-track {
        background-color: transparent; /* 스크롤 트랙을 투명하게 */
      }
      &::-webkit-scrollbar-thumb {
        background-color: transparent; /* 스크롤바 손잡이를 투명하게 */
      }

      li {
        position: static;
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        height: 5vw;
        padding: 0 $left-nav-padding;
        cursor: pointer;
        color: var(--sidebar-text);

        &.active {
          border-radius: 0.7813vw;
          background-color: var(--brand-primary);
          color: #ffffff;
        }

        span {
          font-size: $font-size;
        }

        .name {
          display: block;
          overflow-x: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .caret {
          display: grid;
          align-items: center;
          justify-content: center;

          .arrow {
            display: inline-block;
            padding: 0.3125vw;
            aspect-ratio: 1/1;
            border: solid #fff;
            border-width: 0 0.0781vw 0.0781vw 0;
            transform: rotate(-45deg);
            -webkit-transform: rotate(-45deg);
          }
        }
      }
    }

    footer {
      padding-top: 0.7813vw;
      padding-right: calc(vars.$left-nav-width - $left-nav-bg-width + $left-nav-padding);

      button {
        display: flex;
        gap: 0.3906vw;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 5vw;
        font-size: $font-size;
        color: #fff;
        background: none;
        background-color: #3f4553;
        border: none;
        border-radius: 0.7813vw;

        img {
          width: 2.5vw;
        }
      }
    }
  }
</style>
